import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { Link } from 'react-router';

import { formatAgo } from '@/features/emails/dates';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { ArrowLeftIcon } from '@/shared/ui/icons';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { endSession, featuresOf, isPlatform, useSessionUser } from '@/shared/session/currentUser';
import { MIN_PASSWORD } from '@/shared/session/password';
import {
  CAPABILITY_LABELS,
  CAPABILITY_ORDER,
  type Capability,
  capabilitiesOf,
  PROFILE_HINTS,
  PROFILE_LABELS,
  PROFILE_ORDER,
  type UserProfile,
} from '@/shared/session/profiles';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';

import { historyNarrows, historySentence } from './historyText';
import { changesOf, draftOf, initialsOf, type PersonDraft } from './personChanges';
import type { AdminUser, InviteRead } from './types';
import {
  useInviteUser,
  useProfileCapabilities,
  useResetPassword,
  useResetTwoFactor,
  useUpdateUser,
  useUserHistory,
} from './useAdmin';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' });
const timeFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

/** What the administrator gets to hand over: a link, or a password typed here. */
export type Handout =
  { kind: 'invite'; invite: InviteRead } | { kind: 'password'; email: string; password: string };

/**
 * The card of one person in Usuarios (contract v10.58, ADR 0055 of the back): everything
 * that decides what they see and do, in sections, and below it what has changed and who
 * did it.
 *
 * Mailboxes, profile and the switches are a **draft**: nothing travels until «Guardar
 * cambios», and the bar that offers it says first, one line per change, what the person
 * starts and stops seeing. One `PATCH`, one password of the elevated mode. Before the
 * card, each checkbox saved on its own, and unticking a mailbox by mistake left somebody
 * with an empty inbox without a word.
 *
 * The access actions (password, second factor, deactivate) act at once, each behind its
 * own confirmation, because they are not a setting: they close sessions.
 *
 * What only a superadministrator decides (whether somebody administers, sends the payroll,
 * is a superadministrator) shows to an administrator of the shop as it is, saying who
 * changes it, instead of as a grey checkbox. The rules are the API's (contracts v10.11,
 * v10.16, v10.47, v10.53); the card only explains them before the 403.
 */
export function PersonCard({
  user,
  known,
  onHandout,
  onDirtyChange,
  backTo,
}: {
  user: AdminUser;
  /** The mailboxes that can be assigned. */
  known: string[];
  onHandout: (shown: Handout) => void;
  onDirtyChange: (dirty: boolean) => void;
  /** The list, for the «← Personas» of a phone. */
  backTo: string;
}) {
  const me = useSessionUser();
  const platform = isPlatform(me);
  const self = user.id === me?.id;
  const features = featuresOf(me);
  const capabilities = useProfileCapabilities();
  const mine = capabilitiesOf(me);
  const update = useUpdateUser();
  const [draft, setDraft] = useState<PersonDraft>(() => draftOf(user));
  const [saved, setSaved] = useState(false);
  const { payload, lines } = changesOf(user, draft);
  const dirty = lines.length > 0;

  // The server answered (after a save, or another tab): what was not edited follows it.
  useEffect(() => {
    if (!update.isPending) setDraft(draftOf(user));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the row changes
  }, [user]);

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  function edit(change: Partial<PersonDraft>) {
    setSaved(false);
    update.reset();
    setDraft((current) => ({ ...current, ...change }));
  }

  function save() {
    update.mutate(
      { id: user.id, payload },
      {
        onSuccess: (fresh) => {
          setDraft(draftOf(fresh));
          setSaved(true);
        },
      },
    );
  }

  const superadmin = user.is_superadmin;
  const name = user.full_name ?? user.email;

  return (
    <section
      aria-labelledby={`persona-${user.id}`}
      className="bg-card border-line rounded-card shadow-card flex min-w-0 flex-col border"
    >
      <header className="border-line-2 flex flex-col gap-3 border-b px-4 py-4 sm:px-5">
        <Link to={backTo} className="text-ink-3 flex items-center gap-1 text-[13px] lg:hidden">
          <ArrowLeftIcon className="size-4" /> Personas
        </Link>
        <div className="flex items-start gap-3">
          <Avatar user={user} size="lg" />
          <div className="min-w-0 flex-1">
            <h2 id={`persona-${user.id}`} className="text-ink text-[18px] leading-6 font-semibold">
              {name}
            </h2>
            <p className="text-ink-3 text-[13px] break-all">{user.email}</p>
            <p className="text-ink-3 mt-1 text-[12.5px]">
              Alta el {formatDate(dateFormat, user.created_at)}
              {user.last_seen_at !== undefined ? ' · ' : null}
              <LastSeen value={user.last_seen_at} />
            </p>
          </div>
        </div>
        <StateBadges user={user} />
      </header>

      <div className="flex flex-col divide-y divide-[var(--color-line-2)]">
        <Section title="Nombre">
          <Input
            label="Nombre"
            value={draft.full_name}
            onChange={(event) => edit({ full_name: event.target.value })}
            hint="Como sale en la plataforma y en los avisos."
            className="max-w-sm"
          />
        </Section>

        <Section
          title="Buzones que lee"
          hint="Ve esos buzones enteros y ningún otro. Sin buzón, la bandeja le sale vacía."
        >
          {superadmin ? (
            <p className="text-ink-2 text-[14px]">Todos: es superadministrador.</p>
          ) : (
            <MailboxChecks
              label={`Buzones de ${user.email}`}
              known={known}
              value={draft.mailboxes}
              onChange={(mailboxes) => edit({ mailboxes })}
            />
          )}
        </Section>

        <Section
          title="Perfil"
          hint="Qué ve de los precios, los clientes y los presupuestos. El correo lo deciden sus buzones."
        >
          {superadmin ? (
            <p className="text-ink-2 text-[14px]">Lo ve todo: es superadministrador.</p>
          ) : (
            <ProfileChoice
              label={`Perfil de ${user.email}`}
              value={draft.profile}
              capabilities={capabilities}
              // Nobody changes their own (409), nor gives more than they see (403).
              reasonFor={(profile) => {
                if (self) return 'Tu propio perfil lo cambia otra persona.';
                if (platform) return null;
                const wider = capabilities[profile].some((each) => !mine.has(each));
                return wider ? 'Ve más que tu perfil: solo lo da quien lo ve.' : null;
              }}
              onChange={(profile) => edit({ profile })}
            />
          )}
        </Section>

        <Section title="Permisos">
          <Permission
            label="Administra"
            description="Usuarios, Buzones, Destinos, Contactos y Activación. No amplía el correo que ve."
            checked={draft.is_admin}
            hidden={draft.is_superadmin}
            editable={platform && !self}
            reason={
              self
                ? 'El tuyo lo cambia otro superadministrador.'
                : 'Lo cambia un superadministrador (Ignira).'
            }
            onChange={(is_admin) => edit({ is_admin })}
          />
          {features.payroll === true ? (
            <Permission
              label="Ve y envía las nóminas"
              description="La pantalla de Nóminas. Ni administrar ni ser superadministrador la dan."
              checked={draft.can_send_payroll}
              hidden={draft.is_superadmin}
              editable={platform}
              reason="Lo cambia un superadministrador (Ignira)."
              onChange={(can_send_payroll) => edit({ can_send_payroll })}
            />
          ) : null}
          {platform ? (
            <Permission
              label="Superadministrador"
              description="Ignira: todos los buzones, Accesos, Datos e Incidencias. Solo para quien mantiene la plataforma."
              checked={draft.is_superadmin}
              editable={!self}
              reason="El tuyo lo cambia otro superadministrador."
              onChange={(is_superadmin) =>
                edit(
                  is_superadmin
                    ? { is_superadmin, is_admin: false, can_send_payroll: false }
                    : { is_superadmin },
                )
              }
            />
          ) : null}
        </Section>
      </div>

      {dirty || saved || update.isError ? (
        <SaveBar
          name={name}
          lines={lines}
          saved={saved && !dirty}
          busy={update.isPending}
          error={update.isError ? update.error : null}
          onDiscard={() => {
            update.reset();
            setDraft(draftOf(user));
          }}
          onSave={save}
        />
      ) : null}

      <AccessSection user={user} self={self} onHandout={onHandout} />
      <HistorySection user={user} />
    </section>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2 px-4 py-4 sm:px-5">
      <div>
        <h3 id={id} className="text-ink text-[14px] font-semibold">
          {title}
        </h3>
        {hint ? <p className="text-ink-3 text-[12.5px] leading-snug">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

export function Avatar({
  user,
  size = 'md',
}: {
  user: Pick<AdminUser, 'full_name' | 'email' | 'is_active'>;
  size?: 'md' | 'lg';
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'bg-paper-2 text-ink-2 ring-line inline-flex shrink-0 items-center justify-center rounded-full font-semibold ring-1',
        size === 'lg' ? 'size-11 text-[15px]' : 'size-9 text-[13px]',
        user.is_active ? null : 'opacity-50',
      )}
    >
      {initialsOf(user)}
    </span>
  );
}

export function LastSeen({ value }: { value: string | null | undefined }) {
  // A back older than v10.58 does not send the field: say nothing rather than «nunca».
  if (value === undefined) return null;
  if (value === null) return <span>no ha entrado nunca</span>;
  return <span title={formatDate(timeFormat, value)}>entró {formatAgo(value)}</span>;
}

/** Only the exception is painted: deactivated, first login pending, no second factor. */
export function StateBadges({ user }: { user: AdminUser }) {
  const badges: ReactNode[] = [];
  if (user.is_superadmin)
    badges.push(
      <Badge key="s" tone="accent">
        Superadministrador
      </Badge>,
    );
  if (!user.is_active) badges.push(<Badge key="d">Desactivado</Badge>);
  if (user.must_change_password)
    badges.push(
      <Badge
        key="p"
        tone="warn"
        title="Aún no ha entrado con una contraseña suya: la elegirá con su enlace o en su primer acceso"
      >
        primer acceso pendiente
      </Badge>,
    );
  if (badges.length === 0) return null;
  return <div className="flex flex-wrap gap-1">{badges}</div>;
}

/**
 * One checkbox per mailbox the system reads. An address already assigned that the system
 * no longer reads still shows, marked, so it can be unticked; there is no field to type
 * one, because a typo is an inbox that never fills (contract v10.12).
 */
export function MailboxChecks({
  label,
  known,
  value,
  onChange,
}: {
  label: string;
  known: string[];
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const options = [...new Set([...known, ...value])].sort();
  return (
    <fieldset className="flex flex-col gap-1.5" aria-label={label}>
      {options.length === 0 ? (
        <span className="text-ink-3 text-[13px]">
          Ningún buzón dado de alta. Se dan de alta en{' '}
          <Link to="/administracion/buzones" className="link">
            Buzones
          </Link>
          .
        </span>
      ) : null}
      {options.map((mailbox) => (
        <label key={mailbox} className="text-ink-2 flex items-center gap-2 text-[14px]">
          <input
            type="checkbox"
            className="accent-accent size-4"
            checked={value.includes(mailbox)}
            onChange={(event) =>
              onChange(
                [
                  ...new Set(
                    event.target.checked
                      ? [...value, mailbox]
                      : value.filter((each) => each !== mailbox),
                  ),
                ].sort(),
              )
            }
          />
          <span className="flex flex-col">
            <span className="break-all">{mailbox}</span>
            {known.includes(mailbox) ? null : (
              <span className="text-warn text-[12px]">ya no se lee: quítalo</span>
            )}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/**
 * The three profiles as cards that say what each one sees, the chosen one marked. The
 * list of what each allows is the API's (contract v10.58); an option this person may not
 * give says why under it.
 */
function ProfileChoice({
  label,
  value,
  capabilities,
  reasonFor,
  onChange,
}: {
  label: string;
  value: UserProfile;
  capabilities: Record<UserProfile, readonly Capability[]>;
  reasonFor: (profile: UserProfile) => string | null;
  onChange: (profile: UserProfile) => void;
}) {
  const name = useId();
  return (
    <div role="radiogroup" aria-label={label} className="grid gap-2 sm:grid-cols-3">
      {PROFILE_ORDER.map((profile) => {
        const reason = profile === value ? null : reasonFor(profile);
        const disabled = reason !== null;
        const allows = CAPABILITY_ORDER.filter((each) => capabilities[profile].includes(each));
        const labelId = `${name}-${profile}`;
        return (
          <label
            key={profile}
            className={cn(
              'rounded-card flex cursor-pointer flex-col gap-1.5 border p-3 text-[13px]',
              profile === value
                ? 'border-accent bg-accent-soft/40 ring-accent ring-1'
                : 'border-line hover:bg-paper-2',
              disabled ? 'cursor-not-allowed opacity-60 hover:bg-transparent' : null,
            )}
          >
            <span className="flex items-center gap-2">
              <input
                type="radio"
                name={name}
                aria-labelledby={labelId}
                aria-describedby={`${labelId}-allows`}
                className="accent-accent size-4"
                checked={profile === value}
                disabled={disabled}
                onChange={() => onChange(profile)}
              />
              <span id={labelId} className="text-ink text-[14px] font-medium">
                {PROFILE_LABELS[profile]}
              </span>
            </span>
            <ul
              id={`${labelId}-allows`}
              className="text-ink-2 flex flex-col gap-0.5 pl-6 text-[12.5px] leading-snug"
            >
              {allows.length ? (
                allows.map((each) => <li key={each}>{CAPABILITY_LABELS[each]}</li>)
              ) : (
                <li>{PROFILE_HINTS[profile]}</li>
              )}
            </ul>
            {reason ? <span className="text-ink-3 pl-6 text-[12px]">{reason}</span> : null}
          </label>
        );
      })}
    </div>
  );
}

function Permission({
  label,
  description,
  checked,
  editable,
  hidden = false,
  reason,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  editable: boolean;
  hidden?: boolean;
  /** Why it cannot be changed here, when it cannot. */
  reason: string;
  onChange: (on: boolean) => void;
}) {
  if (hidden) return null;
  if (!editable) {
    return (
      <div className="flex items-start justify-between gap-3 text-[14px]">
        <span className="flex flex-col">
          <span className="text-ink-2">{label}</span>
          <span className="text-ink-3 text-[12.5px] leading-snug">{description}</span>
        </span>
        <span className="flex shrink-0 flex-col items-end text-right">
          <span className="text-ink font-medium">{checked ? 'Sí' : 'No'}</span>
          <span className="text-ink-3 max-w-[14rem] text-[12px] leading-snug">{reason}</span>
        </span>
      </div>
    );
  }
  return (
    <label className="text-ink-2 flex items-start gap-2 text-[14px]">
      <input
        type="checkbox"
        className="accent-accent mt-0.5 size-4"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="flex flex-col">
        {label}
        <span className="text-ink-3 text-[12.5px] leading-snug">{description}</span>
      </span>
    </label>
  );
}

/**
 * What the save will do, before it does. Sticky at the bottom of the screen (above the
 * bottom bar of a phone) so it is in view wherever the change was made.
 */
function SaveBar({
  name,
  lines,
  saved,
  busy,
  error,
  onDiscard,
  onSave,
}: {
  name: string;
  lines: ReturnType<typeof changesOf>['lines'];
  saved: boolean;
  busy: boolean;
  error: unknown;
  onDiscard: () => void;
  onSave: () => void;
}) {
  return (
    <div
      role="region"
      aria-label="Cambios sin guardar"
      className="bg-card border-line sticky bottom-[calc(3.5rem+1px+env(safe-area-inset-bottom))] z-10 flex flex-col gap-2 border-t px-4 py-3 shadow-[0_-4px_20px_0_rgb(0_0_0/0.06)] sm:px-5 lg:bottom-0"
    >
      {saved ? (
        <p role="status" className="text-ok text-[13.5px]">
          Guardado. Vale desde su siguiente pantalla.
        </p>
      ) : (
        <>
          <p className="text-ink text-[13.5px] font-medium">
            {lines.length === 1 ? 'Un cambio' : `${lines.length} cambios`} para {name}, sin guardar:
          </p>
          <ul className="flex flex-col gap-0.5 text-[13px]">
            {lines.map((line) => (
              <li key={line.text} className={line.narrows ? 'text-warn' : 'text-ink-2'}>
                {line.narrows ? '− ' : '+ '}
                {line.text}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={onSave} disabled={busy}>
              {busy ? 'Guardando…' : 'Guardar cambios'}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDiscard} disabled={busy}>
              Descartar
            </Button>
          </div>
        </>
      )}
      {error ? <ErrorMessage error={error}>No se pudo guardar</ErrorMessage> : null}
    </div>
  );
}

/**
 * Password, invitation, second factor and deactivation: each acts at once, behind its own
 * confirmation, because each closes sessions.
 */
function AccessSection({
  user,
  self,
  onHandout,
}: {
  user: AdminUser;
  self: boolean;
  onHandout: (shown: Handout) => void;
}) {
  const me = useSessionUser();
  const platform = isPlatform(me);
  const required = featuresOf(me).two_factor === 'required';
  const queryClient = useQueryClient();
  const update = useUpdateUser();
  const reset = useResetPassword();
  const invite = useInviteUser((issued) => onHandout({ kind: 'invite', invite: issued }));
  const [typing, setTyping] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState<'deactivate' | 'two_factor' | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const resetTwoFactor = useResetTwoFactor((fresh) => {
    setConfirm(null);
    setDone(
      `Doble factor reiniciado. ${required ? 'Lo volverá a activar la próxima vez que entre.' : 'Puede volver a activarlo desde su perfil.'}`,
    );
    // One's own reset closes this session too (contract v10.45).
    if (fresh.id === me?.id) endSession(queryClient, 'two_factor_reset');
  });
  /** Contract v10.53: whoever administers the shop does not reset the account that sees
   * the payroll, or they could enter as that person. The API says 403 anyway. */
  const payrollAccount = user.can_send_payroll === true && !platform && !self;
  const knowsTwoFactor = user.two_factor_enabled !== undefined;

  function submitPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const typed = password;
    reset.mutate(
      { id: user.id, password: typed },
      {
        onSuccess: () => {
          onHandout({ kind: 'password', email: user.email, password: typed });
          setTyping(false);
          setPassword('');
        },
      },
    );
  }

  const name = user.full_name ?? user.email;

  return (
    <Section title="Acceso">
      <AccessRow
        what="Contraseña"
        state={
          user.must_change_password
            ? 'Aún no ha elegido la suya'
            : user.password_changed_at
              ? `Cambiada el ${formatDate(dateFormat, user.password_changed_at)}`
              : 'Puesta'
        }
        reason={payrollAccount ? 'Tiene el permiso de las nóminas: la reinicia Ignira.' : null}
        actions={
          <>
            {platform ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={!user.is_active || invite.isPending}
                onClick={() => invite.mutate(user.id)}
              >
                Nueva invitación
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="secondary"
              disabled={payrollAccount}
              onClick={() => {
                setTyping(true);
                setPassword('');
              }}
            >
              Nueva contraseña
            </Button>
          </>
        }
      />
      {typing ? (
        <form
          onSubmit={submitPassword}
          className="bg-paper-2 rounded-card flex flex-col gap-3 p-3"
          aria-label={`Nueva contraseña para ${user.email}`}
        >
          <Input
            label="Contraseña"
            type="text"
            autoComplete="off"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            hint={`Mínimo ${MIN_PASSWORD} caracteres. Se enseñará una vez; la persona la cambiará al entrar y sus sesiones abiertas se cerrarán.`}
            minLength={MIN_PASSWORD}
            required
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={reset.isPending || password.length < MIN_PASSWORD}
            >
              {reset.isPending ? 'Guardando…' : 'Guardar contraseña'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setTyping(false)}>
              Cancelar
            </Button>
          </div>
          {reset.isError ? (
            <ErrorMessage error={reset.error}>No se pudo cambiar la contraseña</ErrorMessage>
          ) : null}
        </form>
      ) : null}
      {invite.isError ? (
        <ErrorMessage error={invite.error}>No se pudo preparar la invitación</ErrorMessage>
      ) : null}

      {knowsTwoFactor ? (
        <AccessRow
          what="Doble factor"
          state={user.two_factor_enabled ? 'Activado' : 'Sin activar'}
          reason={payrollAccount ? 'Tiene el permiso de las nóminas: lo reinicia Ignira.' : null}
          actions={
            <Button
              size="sm"
              variant="secondary"
              disabled={user.two_factor_enabled !== true || payrollAccount}
              onClick={() => {
                resetTwoFactor.reset();
                setDone(null);
                setConfirm('two_factor');
              }}
            >
              Reiniciar doble factor
            </Button>
          }
        />
      ) : null}

      <AccessRow
        what="Cuenta"
        state={user.is_active ? 'Activa' : 'Desactivada: no puede entrar'}
        reason={self ? 'No puedes desactivarte a ti mismo.' : null}
        actions={
          user.is_active ? (
            <Button
              size="sm"
              variant="danger"
              disabled={self}
              onClick={() => {
                update.reset();
                setConfirm('deactivate');
              }}
            >
              Desactivar
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              disabled={update.isPending}
              onClick={() => update.mutate({ id: user.id, payload: { is_active: true } })}
            >
              Reactivar
            </Button>
          )
        }
      />
      {update.isError && confirm === null ? (
        <ErrorMessage error={update.error}>No se pudo guardar el cambio</ErrorMessage>
      ) : null}
      {done ? (
        <p role="status" className="text-ok text-[13.5px]">
          {done}
        </p>
      ) : null}

      {confirm === 'deactivate' ? (
        <Dialog
          title={`¿Desactivar a ${name}?`}
          size="sm"
          busy={update.isPending}
          onClose={() => setConfirm(null)}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setConfirm(null)}
                disabled={update.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                disabled={update.isPending}
                onClick={() =>
                  update.mutate(
                    { id: user.id, payload: { is_active: false } },
                    { onSuccess: () => setConfirm(null) },
                  )
                }
              >
                {update.isPending ? 'Desactivando…' : 'Desactivar'}
              </Button>
            </>
          }
        >
          <p className="text-ink-2 text-[14.5px] leading-relaxed">
            Deja de poder entrar y se le cierran las sesiones abiertas ahora mismo. No se borra
            nada: sus buzones, su perfil y su historial se quedan, y «Reactivar» la deja como
            estaba.
          </p>
          {update.isError ? <ErrorMessage error={update.error} /> : null}
        </Dialog>
      ) : null}
      {confirm === 'two_factor' ? (
        <ResetTwoFactorDialog
          user={user}
          self={self}
          required={required}
          busy={resetTwoFactor.isPending}
          error={resetTwoFactor.isError ? resetTwoFactor.error : null}
          onConfirm={() => resetTwoFactor.mutate(user.id)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </Section>
  );
}

function AccessRow({
  what,
  state,
  reason,
  actions,
}: {
  what: string;
  state: string;
  reason: string | null;
  actions: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
      <div className="flex min-w-0 flex-col">
        <span className="text-ink-2 text-[14px]">{what}</span>
        <span className="text-ink-3 text-[12.5px]">{state}</span>
        {reason ? <span className="text-ink-3 text-[12px]">{reason}</span> : null}
      </div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}

/**
 * What «Reiniciar doble factor» takes away, before it does: the app, the codes and the
 * remembered devices of that person, and their open sessions. The confirmation is the
 * administrator's; the password of the elevated mode comes after, from `withElevation`.
 */
function ResetTwoFactorDialog({
  user,
  self,
  required,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  user: AdminUser;
  self: boolean;
  required: boolean;
  busy: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      title="¿Reiniciar el doble factor?"
      size="sm"
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Reiniciando…' : 'Reiniciar doble factor'}
          </Button>
        </>
      }
    >
      {self ? (
        <p className="text-ink-2 text-[14.5px] leading-relaxed">
          Se borran tu aplicación del móvil, tus códigos de recuperación y los dispositivos que
          recordabas, y se cierran tus sesiones, también esta: volverás a la puerta de entrada.{' '}
          {required
            ? 'Al entrar verás un código QR nuevo para activarlo otra vez.'
            : 'Después puedes volver a activarlo desde tu perfil.'}
        </p>
      ) : (
        <p className="text-ink-2 text-[14.5px] leading-relaxed">
          A <strong className="text-ink break-all">{user.full_name ?? user.email}</strong> se le
          borran la aplicación del móvil, los códigos de recuperación y los dispositivos que
          recordaba, y se le cierran las sesiones abiertas.{' '}
          {required
            ? 'La próxima vez que entre, después de la contraseña, verá un código QR nuevo para volver a activarlo.'
            : 'Podrá volver a activarlo desde su perfil.'}
        </p>
      )}
      <p className="text-ink-3 text-[13.5px] leading-snug">
        Es para quien ha perdido el móvil y los códigos. Hazlo solo si te lo ha pedido esa persona.
      </p>
      {error ? <ErrorMessage error={error} /> : null}
    </Dialog>
  );
}

/** Who changed what of this person, newest first (contract v10.58). Left out with a back
 * that does not have it. */
function HistorySection({ user }: { user: AdminUser }) {
  const history = useUserHistory(user.id);
  if (history.isError) return null;
  return (
    <Section title="Historial" hint="Lo que ha cambiado de su acceso, quién y cuándo.">
      {history.isPending ? <Spinner label="Cargando el historial" /> : null}
      {history.data && history.data.items.length === 0 ? (
        <p className="text-ink-3 text-[13px]">Nada anotado todavía.</p>
      ) : null}
      {history.data && history.data.items.length > 0 ? (
        <ol className="flex flex-col gap-2.5" aria-label={`Historial de ${user.email}`}>
          {history.data.items.map((entry, index) => (
            <li key={`${entry.at}-${index}`} className="flex gap-2.5 text-[13.5px]">
              <span
                aria-hidden="true"
                className={cn(
                  'mt-1.5 size-2 shrink-0 rounded-full',
                  historyNarrows(entry) ? 'bg-warn' : 'bg-ink-4',
                )}
              />
              <span className="flex flex-col">
                <span className="text-ink-2">{historySentence(entry)}</span>
                <span className="text-ink-3 text-[12px]">
                  {formatDate(timeFormat, entry.at)}
                  {entry.by_self ? ' · lo hizo esta persona' : entry.by ? ` · ${entry.by}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      {history.data?.truncated ? (
        <p className="text-ink-3 text-[12.5px]">Hay cambios más antiguos.</p>
      ) : null}
    </Section>
  );
}
